import { type Tag, QBankStructureLevelCategory } from '@exam-management/graphql';
import { type QBankStructureLevelItemWithLevel } from '@exam-management/qbs-provider';
import { groupBy } from '@shared/utils';

import { type ItemSetQuestionPropertiesData, type QBankStructureDashboardData } from '../../types';
import {
  generateLevelItemRows,
  addItemSetQuestionsToQBankStructureTree,
  generateItemSetQuestionRows,
} from '../../utils';

const LEVEL_ITEMS_FOR_ITEM_SET_ROWS = [
  QBankStructureLevelCategory.Course,
  QBankStructureLevelCategory.Part,
  QBankStructureLevelCategory.Subject,
  QBankStructureLevelCategory.Group,
  QBankStructureLevelCategory.Chapter,
];

export function formatDataToQBankStructureItemSetQuestionDashboardTable(input: {
  qBankStructureLevelItems: QBankStructureLevelItemWithLevel[];
  itemSetQuestionPropertiesData: ItemSetQuestionPropertiesData[];
  difficultyTags: Tag[];
  calculationTags: Tag[];
}): QBankStructureDashboardData[] {
  const {
    qBankStructureLevelItems,
    itemSetQuestionPropertiesData,
    difficultyTags,
    calculationTags,
  } = input;

  const filteredLevelItems = qBankStructureLevelItems.filter((item) =>
    LEVEL_ITEMS_FOR_ITEM_SET_ROWS.includes(item.qBankStructureLevel.category),
  );

  const qbsRows = generateLevelItemRows(filteredLevelItems);

  const difficultyMap = {
    hard: difficultyTags.find((t) => t.name === 'ยาก')?.id ?? '',
    medium: difficultyTags.find((t) => t.name === 'กลาง')?.id ?? '',
    easy: difficultyTags.find((t) => t.name === 'ง่าย')?.id ?? '',
  };

  const calculationMap = {
    calc: calculationTags.find((t) => t.name === 'คำนวณ')?.id ?? '',
    nonCalc: calculationTags.find((t) => t.name === 'ไม่คำนวณ')?.id ?? '',
  };

  const chapterLevelItems = qBankStructureLevelItems.filter(
    (item) => item.qBankStructureLevel.category === QBankStructureLevelCategory.Chapter,
  );

  const itemSetQuestionPropertiesDataGroupByLocationId = groupBy(
    itemSetQuestionPropertiesData,
    'locationId',
  );

  const itemSetQuestionPropertiesDataByChapter = itemSetQuestionPropertiesData.filter((item) =>
    chapterLevelItems.some((chapter) => chapter.id === item.locationId),
  );

  const itemSetQuestionRows = [
    ...generateItemSetQuestionRows(
      itemSetQuestionPropertiesDataByChapter,
      difficultyMap,
      calculationMap,
    ),
  ].sort((rowA, rowB) => (rowA.size ?? 0) - (rowB.size ?? 0));

  const qbsRowsWithQuestionProperties = qbsRows.map((item) => {
    const itemSetQuestionPropertiesDataInLocation =
      itemSetQuestionPropertiesDataGroupByLocationId[item.id] ?? [];

    if (itemSetQuestionPropertiesDataInLocation.length === 0) return item;

    const sizeAndTotal = itemSetQuestionPropertiesDataInLocation.flatMap((itemSetQuestion) =>
      itemSetQuestion.questionProperties.reduce(
        (sum, property) => {
          return {
            total: sum.size + itemSetQuestion.size,
            size: sum.total + property.totalQuestions,
          };
        },
        {
          size: 0,
          total: 0,
        },
      ),
    );

    const { size, total } = sizeAndTotal.reduce(
      (sum, item) => {
        return {
          size: sum.size + item.size,
          total: sum.total + item.total,
        };
      },
      {
        size: 0,
        total: 0,
      },
    );

    return {
      ...item,
      size,
      total,
    };
  });

  const qbsTreeWithLosAndItemSetQuestions = addItemSetQuestionsToQBankStructureTree(
    qbsRowsWithQuestionProperties,
    itemSetQuestionRows,
  );

  const itemsById = new Map<string, QBankStructureDashboardData>();
  const childrenByParentId = new Map<string, QBankStructureDashboardData[]>();
  for (const item of qbsTreeWithLosAndItemSetQuestions) {
    itemsById.set(item.id, item);

    if (!item.parentId) continue;

    const children = childrenByParentId.get(item.parentId);
    if (children) {
      children.push(item);
    } else {
      childrenByParentId.set(item.parentId, [item]);
    }
  }

  const cache = new Map<string, { questionGroupCount: number; total: number }>();

  return [...qbsTreeWithLosAndItemSetQuestions].map((item) => {
    if (item.level === 'item-set-question') return item;

    const { questionGroupCount, total } = computeItemSetTotal({
      id: item.id,
      itemsById,
      childrenByParentId,
      cache,
    });
    return {
      ...item,
      size: questionGroupCount > 0 ? questionGroupCount : undefined,
      total: total > 0 ? total : undefined,
    };
  });
}

function computeItemSetTotal(input: {
  id: string;
  itemsById: Map<string, QBankStructureDashboardData>;
  childrenByParentId: Map<string, QBankStructureDashboardData[]>;
  cache: Map<string, { questionGroupCount: number; total: number }>;
}): { questionGroupCount: number; total: number } {
  const { id, itemsById, childrenByParentId, cache } = input;

  const cached = cache.get(id);
  if (cached) return cached;

  const item = itemsById.get(id);
  if (!item) return { questionGroupCount: 0, total: 0 };

  if (item.level === 'item-set-question') {
    const result = {
      questionGroupCount: item.total ?? 0,
      total: (item.total ?? 0) * (item.size ?? 0),
    };
    cache.set(id, result);
    return result;
  }

  const children = childrenByParentId.get(id) ?? [];
  const result = children.reduce(
    (acc, child) => {
      const childValues = computeItemSetTotal({
        id: child.id,
        itemsById,
        childrenByParentId,
        cache,
      });
      return {
        questionGroupCount: acc.questionGroupCount + childValues.questionGroupCount,
        total: acc.total + childValues.total,
      };
    },
    { questionGroupCount: 0, total: 0 },
  );

  cache.set(id, result);
  return result;
}
